import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { basicEmployeesAndWages } from 'store/entities/professionalReport/selectors/basicDataSelectors';

import ComposedChart from 'components/reusable/graphs/ComposedChart';

import { Col, Row, Card } from 'antd';

import { BarChartOutlined } from '@ant-design/icons';
import NumberOfEmployeesCombinedLabel from 'components/reusable/graphs/ComposedChart/NumberOfEmployeesCombinedLabel';
import { BlockHeader } from '../../../components/reusable/BlockHeader';
import useNumberFormatter from '../../../hooks/format/useNumberFormatter';
import { ReactComponent as AverageWagesIcon } from 'assets/img/icons/cardIcons/averageWages.svg';
import { CompanyDataRow } from '../../../components/reusable/CompanyDataCard';
import { ReactComponent as NoteIcon } from 'assets/img/icons/cardIcons/note.svg';
import CollapsibleTable from '../../../components/reusable/CollapsibleTable';
import useFormatDate from '../../../hooks/format/useFormatDate';
import useIsUiHidden from '../../../hooks/dom/useIsUiHidden';

const Wages = () => {
  const { t } = useTranslation();
  const { wages, companyEmployeesSectorsNumbers, companyAndSectorsAverageWages } = useSelector(basicEmployeesAndWages);
  const formatNumber = useNumberFormatter({ minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatDate = useFormatDate();
  const isUiHidden = useIsUiHidden();

  const columns = [
    {
      width: isUiHidden ? 80 : null,
      key: 'date',
      dataIndex: 'date',
      title: t('professionalReport.basicData.employees.table.date'),
      render: value => formatDate(value),
    },
    {
      key: 'average',
      dataIndex: 'average',
      title: t('professionalReport.basicData.employees.table.companyAverageWage'),
      render: value => (value ? formatNumber(value) : '-'),
    },
    {
      key: 'averageFemale',
      dataIndex: 'averageFemale',
      title: t('professionalReport.basicData.employees.table.averageFemale'),
      render: value => (value ? formatNumber(value) : '-'),
    },
    {
      key: 'averageMale',
      dataIndex: 'averageMale',
      title: t('professionalReport.basicData.employees.table.averageMale'),
      render: value => (value ? formatNumber(value) : '-'),
    },
    {
      key: 'averageSector',
      dataIndex: 'averageSector',
      title: t('professionalReport.basicData.employees.table.sectorAverageWage'),
      render: value => (value ? formatNumber(value) : '-'),
    },
  ];

  return (
    <div>
      <Card className="card-simple card-simple-padding-none space-vertical-sm has-shadow" style={{ width: '100%' }}>
        <Row
          justify="start"
          align="middle"
          className="no-break"
          style={{ pageBreakInside: 'avoid', pageBreakAfter: 'auto' }}
        >
          <Col>
            <BlockHeader
              icon={<AverageWagesIcon />}
              title={t('professionalReport.basicData.employees.casNumberOfEmployees.title')}
            />
          </Col>
        </Row>
        <Row className="no-break">
          <Col md={24} lg={24}>
            <ComposedChart
              id="casNumberOfEmployees"
              series={[
                {
                  type: 'bar',
                  dataKey: 'employeesNumber',
                  fill: '#333333',
                },
                {
                  type: 'line',
                  dataKey: 'employeesNumberInSectors',
                  stroke: '#BEBEBE',
                },
              ]}
              data={companyEmployeesSectorsNumbers?.numberOfEmployeesInThePeriods ?? []}
              CustomLabel={<NumberOfEmployeesCombinedLabel />}
              lazyLoaded
            />
          </Col>
        </Row>
      </Card>
      <Card className="card-simple card-simple-padding-none space-vertical-sm has-shadow" style={{ width: '100%' }}>
        <Row
          justify="start"
          align="middle"
          className="no-break"
          style={{ pageBreakInside: 'avoid', pageBreakAfter: 'auto' }}
        >
          <Col>
            <BlockHeader
              icon={<BarChartOutlined />}
              title={t('professionalReport.basicData.employees.casAverageWages.title')}
            />
          </Col>
        </Row>
        {companyAndSectorsAverageWages?.averageWagesInThePeriods?.length ? (
          <CollapsibleTable
            title={t('global.detailedInformation')}
            bordered
            data={wages}
            columns={columns}
            className="space-bottom-md"
          />
        ) : null}
        <Row className="no-break">
          <Col md={24} lg={24}>
            <ComposedChart
              id="casAverageWages"
              series={[
                {
                  dataKey: 'averageMale',
                  index: 0,
                  fill: '#0C9CB2',
                },
                {
                  type: 'bar',
                  dataKey: 'companyAverageWage',
                  fill: '#333333',
                },
                {
                  dataKey: 'averageFemale',
                  index: 0,
                  fill: '#999999',
                },
                {
                  type: 'line',
                  dataKey: 'sectorAverageWage',
                  stroke: '#C40F11',
                },
              ]}
              monotone
              activeDefs
              data={companyAndSectorsAverageWages?.averageWagesInThePeriods ?? []}
              hideLabels
              lazyLoaded
            />
          </Col>
        </Row>
        {companyAndSectorsAverageWages?.averageWagesInThePeriods?.length ? (
          <Row gutter={[16]}>
            <Col>
              <BlockHeader title={t('global.note')} icon={<NoteIcon />} />
              <CompanyDataRow
                extraClass="space-bottom-sm fs-12"
                disclaimer={t('professionalReport.basicData.employees.note')}
              />
            </Col>
          </Row>
        ) : null}
      </Card>
    </div>
  );
};

export default Wages;
